// propeller-art.ts — self-contained frontend module: propeller art JSON -> SVG/PNG.
// Zero dependencies; drop this single file into any TS project (or import the
// compiled dist/). It is a typed port of src/{model,svg,pa}.js rendering —
// test/tsparity.test.mjs asserts its SVG output is byte-identical to the core.
//
// Terminology: a *prop* (*.prop.json) is one source art piece (a mesh doc);
// a *PA* (*.pa.json, "propeller arrangement") poses props into a composition.
// Game frontends render PAs, never bare props — the PA carries size/rot/pos.
//
//   import { normalizeProp, normalizePA, paToSVG, colorSet } from './propeller-art';
//   const pa = normalizePA(await (await fetch('pick.pa.json')).json());
//   el.innerHTML = paToSVG(pa, props, schemes, { colors: { coat: '#8a5a1f' } });

// ---------- prop types (the *.prop.json format: one source art piece) ----------
/** '#rrggbb', '#rrggbbaa', or a palette slot name (slots may reference slots). */
export type ColorValue = string;
export type Palette = Record<string, ColorValue>;
/** { sets: { iron: {...} } } or a bare name -> set map. */
export type ColorSchemes = { sets: Record<string, Palette> } | Record<string, Palette>;

export interface Vert { id: number; x: number; y: number; hidden: boolean }
export interface Style {
  fillOn: boolean; fill: ColorValue;
  strokeOn: boolean; stroke: ColorValue; strokeWidth: number;
  strokeMode: 'boundary' | 'all';
  strokeAbove: boolean;
}
export interface PropObject {
  id: number; name: string; layerId: number; hidden: boolean;
  subdiv: number;
  verts: Vert[]; edges: [number, number][]; faces: number[][];
  style: Style;
}
export interface PropEditLayer { id: number; name: string; visible: boolean }
export interface Prop {
  version: number; nextId: number;
  canvas: { w: number; h: number; bg: ColorValue };
  palette: Palette;
  colorSource?: string | null;
  layers: PropEditLayer[];
  objects: PropObject[];
}

// ---------- PA types (the *.pa.json format: an arrangement of props) ----------
export interface PALayer {
  prop?: string;                     // prop path, relative to the PA file
  svg?: string;                      // OR a raw SVG file (hand art with no master)
  set: string | null;                // color set name from colorSource (prop layers)
  pose: [number, number, number, number]; // [scale, rotDeg, dx, dy]
  hidden: boolean;
}
export interface PA {
  version: number;
  world: number;                     // square working box, units (default 128)
  colorSource: string | null;
  layers: PALayer[];
}

// defaults mirror theme.js (kept inline so this file stays dependency-free)
const DEFAULT_FILL = '#c8c8c8';
const DEFAULT_STROKE = '#141414';
const DEFAULT_CANVAS_BG = '#ffffff';
export const DEFAULT_WORLD = 128;

// ---------- normalization ----------
export function defaultStyle(): Style {
  return {
    fillOn: true, fill: DEFAULT_FILL,
    strokeOn: true, stroke: DEFAULT_STROKE,
    strokeWidth: 4, strokeMode: 'boundary',
    strokeAbove: false,
  };
}

/** Fill in fields missing from props saved by older versions (parsed JSON in). */
export function normalizeProp(raw: unknown): Prop {
  const prop = raw as Prop;
  prop.canvas = { w: 1024, h: 1024, bg: DEFAULT_CANVAS_BG, ...(prop.canvas as Partial<Prop['canvas']> | undefined) };
  prop.palette ??= {};
  prop.layers ??= [];
  prop.objects ??= [];
  for (const o of prop.objects) {
    o.subdiv ??= 3;
    o.hidden ??= false;
    o.style = { ...defaultStyle(), ...o.style };
    if (o.style.fill === '#e8e4da') o.style.fill = DEFAULT_FILL; // pre-0.2 near-invisible default
    for (const v of o.verts) v.hidden ??= false;
  }
  return prop;
}

export function normalizePA(raw: unknown): PA {
  const pa = raw as PA;
  pa.version ??= 1;
  pa.world ??= DEFAULT_WORLD;
  pa.colorSource ??= null;
  pa.layers ??= [];
  for (const l of pa.layers) {
    const [s = 1, r = 0, dx = 0, dy = 0] = l.pose ?? [];
    l.pose = [s, r, dx, dy];
    l.set ??= null;
    l.hidden ??= false;
    const legacy = (l as PALayer & { doc?: string }).doc; // pre-rename files
    if (legacy && !l.prop) { l.prop = legacy; delete (l as PALayer & { doc?: string }).doc; }
  }
  return pa;
}

// ---------- palette / color sets ----------
/** Resolve a style color: follow slot-reference chains; unknown/cyclic -> gray. */
export function resolveColor(palette: Palette | null | undefined, c: ColorValue): string {
  const seen = new Set<string>();
  while (typeof c === 'string' && !c.startsWith('#')) {
    if (!palette || !(c in palette) || seen.has(c)) return '#888888';
    seen.add(c);
    c = palette[c];
  }
  return c;
}

/** Flatten a palette so every slot maps to a concrete color. */
export function resolvePalette(palette: Palette | null | undefined): Palette {
  const out: Palette = {};
  for (const k of Object.keys(palette ?? {})) out[k] = resolveColor(palette, k);
  return out;
}

/**
 * The palette a prop renders with under a named color set: set entries override
 * the prop's defaults; un-overridden slots keep their defaults — including
 * references, which re-resolve against the overridden slots (e.g.
 * ore_secondary: 'base' picks up the set's base).
 */
export function effectivePalette(propPalette: Palette, set: Palette): Palette {
  return { ...propPalette, ...set };
}

export function colorSet(schemes: ColorSchemes | null | undefined, name: string): Palette {
  const sets = (schemes as { sets?: Record<string, Palette> })?.sets ?? (schemes as Record<string, Palette>) ?? {};
  if (!(name in sets)) throw new Error(`no color set "${name}" (have: ${Object.keys(sets).join(', ') || 'none'})`);
  return sets[name];
}

// ---------- mesh geometry (Chaikin subdivision + line-art outlines) ----------
interface Pt { x: number; y: number }

const edgeKey = (a: number, b: number) => (a < b ? a + '_' + b : b + '_' + a);
const vmap = (obj: PropObject) => new Map(obj.verts.map(v => [v.id, v]));

function faceUse(obj: PropObject): Map<string, number> {
  const use = new Map<string, number>();
  for (const f of obj.faces) {
    for (let i = 0; i < f.length; i++) {
      const k = edgeKey(f[i], f[(i + 1) % f.length]);
      use.set(k, (use.get(k) ?? 0) + 1);
    }
  }
  return use;
}

/** Chain a set of edges into open paths and closed loops of vert ids. */
function chainEdges(edges: [number, number][]): { open: number[][]; closed: number[][] } {
  const adj = new Map<number, { to: number; i: number }[]>();
  edges.forEach(([a, b], i) => {
    if (!adj.has(a)) adj.set(a, []);
    if (!adj.has(b)) adj.set(b, []);
    adj.get(a)!.push({ to: b, i });
    adj.get(b)!.push({ to: a, i });
  });
  const used = new Array(edges.length).fill(false);
  const open: number[][] = [], closed: number[][] = [];
  const walkFrom = (start: number) => {
    const chain = [start];
    let cur = start;
    for (;;) {
      const nxt = (adj.get(cur) ?? []).find(x => !used[x.i]);
      if (!nxt) break;
      used[nxt.i] = true;
      chain.push(nxt.to);
      cur = nxt.to;
    }
    return chain;
  };
  // open chains start at verts whose degree isn't 2 (endpoints / junctions)
  for (const [v, list] of adj) {
    if (list.length === 2) continue;
    for (const x of list) {
      if (used[x.i]) continue;
      const chain = walkFrom(v);
      if (chain.length > 1) open.push(chain);
    }
  }
  // whatever remains is pure cycles
  edges.forEach(([a], i) => {
    if (used[i]) return;
    const chain = walkFrom(a);
    if (chain.length > 2 && chain[0] === chain[chain.length - 1]) {
      chain.pop();
      closed.push(chain);
    } else if (chain.length > 1) {
      open.push(chain);
    }
  });
  return { open, closed };
}

/** Chaikin corner-cutting — the 2D analog of the Subdivision Surface modifier. */
function chaikin(pts: Pt[], closed: boolean, levels: number): Pt[] {
  let p = pts;
  for (let l = 0; l < levels; l++) {
    if (p.length < 3) break;
    const out: Pt[] = [];
    const n = p.length;
    const cut = (A: Pt, B: Pt) => {
      out.push({ x: A.x * 0.75 + B.x * 0.25, y: A.y * 0.75 + B.y * 0.25 });
      out.push({ x: A.x * 0.25 + B.x * 0.75, y: A.y * 0.25 + B.y * 0.75 });
    };
    if (closed) {
      for (let i = 0; i < n; i++) cut(p[i], p[(i + 1) % n]);
    } else {
      out.push(p[0]);
      for (let i = 0; i < n - 1; i++) cut(p[i], p[i + 1]);
      out.push(p[n - 1]);
    }
    p = out;
  }
  return p;
}

function pointInLoops(loops: Pt[][], x: number, y: number): boolean {
  let inside = false;
  for (const loop of loops) {
    for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
      const a = loop[i], b = loop[j];
      if ((a.y > y) !== (b.y > y) &&
          x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) {
        inside = !inside;
      }
    }
  }
  return inside;
}

interface OutlineGeometry {
  fillLoops: Pt[][];
  strokeLoops: Pt[][];
  strokeOpens: Pt[][];
  suppressedOpens: Pt[][];
  interiorChains: { pts: Pt[]; closed: boolean }[];
}

/** The renderable geometry of an object (see src/model.js for the full notes). */
export function outlineGeometry(obj: PropObject): OutlineGeometry {
  const vm = vmap(obj);
  const use = faceUse(obj);
  const boundary: [number, number][] = [], wire: [number, number][] = [], interior: [number, number][] = [];
  for (const e of obj.edges) {
    const c = use.get(edgeKey(e[0], e[1])) ?? 0;
    if (c === 0) wire.push(e);
    else if (c === 1) boundary.push(e);
    else interior.push(e);
  }
  const lvl = Math.max(0, Math.min(5, obj.subdiv | 0));
  const b = chainEdges(boundary);
  const w = chainEdges(wire);
  const iC = chainEdges(interior);

  // Limit position of each closed-boundary-loop vert on the Chaikin
  // (quadratic B-spline) curve: (prev + 6*v + next) / 8.
  const limit = new Map<number, Pt>();
  if (lvl > 0) {
    for (const loop of b.closed) {
      const n = loop.length;
      for (let i = 0; i < n; i++) {
        if (limit.has(loop[i])) continue;
        const v = vm.get(loop[i]), p = vm.get(loop[(i - 1 + n) % n]), q = vm.get(loop[(i + 1) % n]);
        if (!v || !p || !q) continue;
        limit.set(loop[i], { x: (p.x + 6 * v.x + q.x) / 8, y: (p.y + 6 * v.y + q.y) / 8 });
      }
    }
  }
  const raw = (id: number): Pt | null => { const v = vm.get(id); return v ? { x: v.x, y: v.y } : null; };
  const pts = (ids: number[], snapEnds: boolean) => ids
    .map((id, i) => ((snapEnds && (i === 0 || i === ids.length - 1) && limit.get(id)) || raw(id)))
    .filter((p): p is Pt => !!p);
  const smooth = (ids: number[], closed: boolean, snapEnds = false) => chaikin(pts(ids, snapEnds), closed, lvl);

  const fillLoops = b.closed.map(c => smooth(c, true));

  // Open wire chains fully inside the filled region with both ends on the
  // silhouette are construction diagonals -> hidden in Outline-only mode.
  const wireOpens: Pt[][] = [], suppressedOpens: Pt[][] = [];
  for (const chain of w.open) {
    const sm = smooth(chain, false, true);
    const a = sm[Math.floor((sm.length - 1) / 2)], z = sm[Math.ceil((sm.length - 1) / 2)];
    const mid = { x: (a.x + z.x) / 2, y: (a.y + z.y) / 2 };
    const bothOnBoundary = limit.has(chain[0]) && limit.has(chain[chain.length - 1]);
    if (bothOnBoundary && fillLoops.length && pointInLoops(fillLoops, mid.x, mid.y)) {
      suppressedOpens.push(sm);
    } else {
      wireOpens.push(sm);
    }
  }

  return {
    fillLoops,
    strokeLoops: [...fillLoops, ...w.closed.map(c => smooth(c, true))],
    strokeOpens: [...b.open.map(c => smooth(c, false, true)), ...wireOpens],
    suppressedOpens,
    interiorChains: [
      ...iC.open.map(c => ({ pts: smooth(c, false, true), closed: false })),
      ...iC.closed.map(c => ({ pts: smooth(c, true), closed: true })),
    ],
  };
}

// ---------- prop -> SVG ----------
const esc = (s: string) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
const round2 = (n: number) => Math.round(n * 100) / 100;

const pathOf = (pts: Pt[], close: boolean) =>
  pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${round2(p.x)} ${round2(p.y)}`).join('') + (close ? 'Z' : '');

function fillParts(obj: PropObject, g: OutlineGeometry, indent: string, palette: Palette): string[] {
  const s = obj.style;
  const out: string[] = [];
  if (!s.fillOn) return out;
  const fill = resolveColor(palette, s.fill);
  if (g.fillLoops.length) {
    const d = g.fillLoops.map(l => pathOf(l, true)).join('');
    out.push(`${indent}<path d="${d}" fill="${esc(fill)}" fill-rule="evenodd"/>`);
  } else {
    const vm = vmap(obj);
    for (const f of obj.faces) {
      const pts = f.map(id => vm.get(id)).filter((v): v is Vert => !!v)
        .map(v => `${round2(v.x)},${round2(v.y)}`).join(' ');
      out.push(`${indent}<polygon points="${pts}" fill="${esc(fill)}"/>`);
    }
  }
  return out;
}

function strokeParts(obj: PropObject, g: OutlineGeometry, indent: string, palette: Palette): string[] {
  const s = obj.style;
  if (!s.strokeOn || !(s.strokeWidth > 0)) return [];
  let d = g.strokeLoops.map(l => pathOf(l, true)).join('') +
          g.strokeOpens.map(c => pathOf(c, false)).join('');
  if (s.strokeMode === 'all') {
    d += g.suppressedOpens.map(c => pathOf(c, false)).join('') +
         g.interiorChains.map(c => pathOf(c.pts, c.closed)).join('');
  }
  if (!d) return [];
  return [`${indent}<path d="${d}" fill="none" stroke="${esc(resolveColor(palette, s.stroke))}" stroke-width="${s.strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`];
}

export interface RenderOpts {
  transparent?: boolean;
  /** Explicit palette override (wins over set/schemes). */
  palette?: Palette | null;
  /** Convenience: recolor with a named color set from `schemes`. */
  set?: string | null;
  schemes?: ColorSchemes | null;
}

function paletteFor(propPalette: Palette, opts: RenderOpts): Palette {
  if (opts.palette) return resolvePalette(opts.palette);
  if (opts.set) return resolvePalette(effectivePalette(propPalette, colorSet(opts.schemes, opts.set)));
  return resolvePalette(propPalette);
}

export function propToSVG(prop: Prop, opts: RenderOpts = {}): string {
  const { w, h, bg } = prop.canvas;
  const palette = paletteFor(prop.palette, opts);
  const parts: string[] = [];
  parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`);
  if (!opts.transparent) parts.push(`  <rect width="${w}" height="${h}" fill="${esc(resolveColor(palette, bg))}"/>`);
  const above: [PropObject, OutlineGeometry][] = []; // strokeAbove strokes render after every layer
  for (const layer of prop.layers) {
    if (!layer.visible) continue;
    parts.push(`  <g data-layer="${esc(layer.name)}">`);
    for (const obj of prop.objects) {
      if (obj.layerId !== layer.id || obj.hidden) continue;
      const g = outlineGeometry(obj);
      parts.push(`    <g data-object="${esc(obj.name)}">`);
      parts.push(...fillParts(obj, g, '      ', palette));
      if (obj.style.strokeAbove) above.push([obj, g]);
      else parts.push(...strokeParts(obj, g, '      ', palette));
      parts.push('    </g>');
    }
    parts.push('  </g>');
  }
  if (above.length) {
    parts.push('  <g data-layer="strokes-above">');
    for (const [obj, g] of above) parts.push(...strokeParts(obj, g, '    ', palette));
    parts.push('  </g>');
  }
  parts.push('</svg>');
  return parts.join('\n');
}

// ---------- PA -> SVG ----------
const round3 = (n: number) => Math.round(n * 1000) / 1000;

/** The path a layer poses: a prop or a raw SVG file. */
export const layerRef = (l: PALayer): string => (l.prop ?? l.svg)!;

/** Natural size of a raw-SVG layer: width/height attrs, else viewBox, else 64. */
export function svgDims(text: string): { w: number; h: number } {
  const attr = (n: string) => {
    const m = text.match(new RegExp(`<svg[^>]*\\b${n}="([\\d.]+)`));
    return m ? Number(m[1]) : null;
  };
  let w = attr('width'), h = attr('height');
  if (w == null || h == null) {
    const vb = text.match(/<svg[^>]*\bviewBox="[\d.\- ]+?[ ,]([\d.]+)[ ,]([\d.]+)"/);
    if (vb) { w ??= Number(vb[1]); h ??= Number(vb[2]); }
  }
  return { w: w ?? 64, h: h ?? 64 };
}

/** Inner markup of a raw SVG file (no outer <svg> element). */
export const svgInnerOf = (text: string): string =>
  text.slice(text.indexOf('>', text.indexOf('<svg')) + 1, text.lastIndexOf('</svg>'));

/** SVG transform placing a layer's content box in the PA's world box. */
export function layerTransform(pa: PA, layer: PALayer, sized: Prop | { w: number; h: number }): string {
  const W = pa.world;
  const { w, h } = 'canvas' in sized ? sized.canvas : sized;
  const [s, r, dx, dy] = layer.pose;
  const k = (s * W) / Math.max(w, h);
  return `translate(${round3(W / 2 + dx)} ${round3(W / 2 + dy)}) rotate(${round3(r)}) ` +
         `scale(${round3(k)}) translate(${round3(-w / 2)} ${round3(-h / 2)})`;
}

/**
 * The palette a PA layer renders its prop with. `colors` is the caller's
 * Record<slot, color> override — it wins over the set and the prop defaults.
 */
export function layerPalette(layer: PALayer, prop: Prop, schemes: ColorSchemes | null, colors: Palette | null = null): Palette {
  let pal = layer.set ? effectivePalette(prop.palette, colorSet(schemes, layer.set)) : prop.palette;
  if (colors && Object.keys(colors).length) pal = { ...pal, ...colors };
  return pal;
}

/** Inner markup of a prop's SVG (no outer <svg> element), for embedding in a <g>. */
export function propSVGInner(prop: Prop, palette: Palette | null = null): string {
  const svg = propToSVG(prop, { transparent: true, palette });
  return svg.slice(svg.indexOf('\n') + 1, svg.lastIndexOf('</svg>')).trimEnd();
}

/** Values: normalized props for {prop} layers, raw SVG text for {svg} layers. */
export type PropMap = Map<string, Prop | string> | Record<string, Prop | string>;
const getPropFrom = (props: PropMap, path: string): Prop | string | undefined =>
  props instanceof Map ? props.get(path) : props[path];

export interface PARenderOpts {
  background?: string | null;
  /** Named-color overrides applied to every prop layer (wins over sets). */
  colors?: Palette | null;
  /** Cache each prop layer's inner SVG per (prop, set, colors) — poses stay live. */
  memo?: boolean;
}

/**
 * Compose a PA into one SVG. props: layer path -> normalized prop (or raw
 * SVG text for {svg} layers). This is THE frontend entry point: game code
 * renders PAs, never bare props. memo is the right mode for animation /
 * repeated recomposition.
 */
export function paToSVG(pa: PA, props: PropMap, schemes: ColorSchemes | null = null,
                        { background = null, colors = null, memo = false }: PARenderOpts = {}): string {
  const W = pa.world;
  const parts = [`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${W}" viewBox="0 0 ${W} ${W}">`];
  if (background) parts.push(`  <rect width="${W}" height="${W}" fill="${background}"/>`);
  for (const layer of pa.layers) {
    if (layer.hidden) continue;
    const res = getPropFrom(props, layerRef(layer));
    if (!res) throw new Error(`PA layer not loaded: "${layerRef(layer)}"`);
    if (layer.svg) {
      parts.push(`  <g data-svg="${layer.svg}" transform="${layerTransform(pa, layer, svgDims(res as string))}">`);
      parts.push(svgInnerOf(res as string).trim());
    } else {
      const prop = res as Prop;
      parts.push(`  <g data-prop="${layer.prop}" transform="${layerTransform(pa, layer, prop)}">`);
      parts.push(memo ? propSVGInnerMemo(prop, layer, schemes, colors)
                      : propSVGInner(prop, layerPalette(layer, prop, schemes, colors)));
    }
    parts.push('  </g>');
  }
  parts.push('</svg>');
  return parts.join('\n');
}

// ---------- memoization ----------
// Keyed on prop object identity (WeakMap) + render options, so a reloaded prop
// (new object) never hits a stale entry and dropped props free their cache.
// If you mutate a prop in place, call invalidateProp(prop). Assumes the schemes
// object for a given set name is stable (sets are keyed by name only).
const svgCache = new WeakMap<Prop, Map<string, string>>();

function cached(prop: Prop, key: string, make: () => string): string {
  let m = svgCache.get(prop);
  if (!m) svgCache.set(prop, m = new Map());
  let v = m.get(key);
  if (v === undefined) m.set(key, v = make());
  return v;
}

export function invalidateProp(prop: Prop): void {
  svgCache.delete(prop);
}

/** Memoized propToSVG. An explicit opts.palette bypasses the cache. */
export function propToSVGMemo(prop: Prop, opts: RenderOpts = {}): string {
  if (opts.palette) return propToSVG(prop, opts);
  return cached(prop, `svg|${opts.transparent ? 1 : 0}|${opts.set ?? ''}`, () => propToSVG(prop, opts));
}

const colorsKey = (colors: Palette | null): string =>
  colors && Object.keys(colors).length
    ? Object.keys(colors).sort().map(k => `${k}:${colors[k]}`).join(',')
    : '';

function propSVGInnerMemo(prop: Prop, layer: PALayer, schemes: ColorSchemes | null, colors: Palette | null): string {
  return cached(prop, `inner|${layer.set ?? ''}|${colorsKey(colors)}`,
    () => propSVGInner(prop, layerPalette(layer, prop, schemes, colors)));
}

// ---------- SVG -> PNG (browser only: needs Image/canvas) ----------
export async function svgToPNGBlob(svg: string, widthPx: number, heightPx: number = widthPx): Promise<Blob> {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('SVG decode failed'));
      img.src = url;
    });
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(widthPx));
    c.height = Math.max(1, Math.round(heightPx));
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    return await new Promise<Blob>((res, rej) =>
      c.toBlob(b => (b ? res(b) : rej(new Error('canvas.toBlob failed'))), 'image/png'));
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function propToPNGBlob(prop: Prop, opts: RenderOpts & { scale?: number } = {}): Promise<Blob> {
  const s = opts.scale ?? 1;
  return svgToPNGBlob(propToSVG(prop, opts), prop.canvas.w * s, prop.canvas.h * s);
}

export async function paToPNGBlob(
  pa: PA, props: PropMap, schemes: ColorSchemes | null = null,
  { scale = 1, background = null, colors = null }: { scale?: number; background?: string | null; colors?: Palette | null } = {},
): Promise<Blob> {
  return svgToPNGBlob(paToSVG(pa, props, schemes, { background, colors }), pa.world * scale);
}
